import React from 'react'

interface TrcArrowLogoProps extends React.SVGProps<SVGSVGElement> {
  size?: number | string
  className?: string
  gradient?: boolean
  withGlow?: boolean
}

/**
 * Official TRC Arrow geometric vector logo mark rendered purely in code (SVG).
 * Built with crisp geometry, vibrant TRC green gradient, subtle drop glow,
 * and high-visibility corporate aesthetics.
 */
export function TrcArrowLogo({
  size = 28,
  className = '',
  gradient = true,
  withGlow = true,
  ...props
}: TrcArrowLogoProps) {
  const gradId = React.useId()
  const glowId = React.useId()

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`inline-block ${className}`}
      {...props}
    >
      <defs>
        {/* Vibrant TRC Corporate Green Gradient */}
        <linearGradient id={gradId} x1="10" y1="10" x2="90" y2="90" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#7ae048" />
          <stop offset="45%" stopColor="#6ebe48" />
          <stop offset="100%" stopColor="#489924" />
        </linearGradient>

        {/* Ambient Soft Glow for maximum contrast and pop */}
        {withGlow && (
          <filter id={glowId} x="-20%" y="-20%" width="140%" height="140%" filterUnits="userSpaceOnUse">
            <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#6ebe48" floodOpacity="0.42" />
          </filter>
        )}
      </defs>

      {/* Main TRC Arrow Shape: Inverted-L / Top-Right Corner */}
      <path
        d="M 10 16 C 10 12.686 12.686 10 16 10 L 84 10 C 87.314 10 90 12.686 90 16 L 90 84 C 90 87.314 87.314 90 84 90 L 65 90 C 62.239 90 60 87.761 60 85 L 60 45 C 60 42.239 57.761 40 55 40 L 15 40 C 12.239 40 10 37.761 10 35 Z"
        fill={gradient ? `url(#${gradId})` : 'currentColor'}
        filter={withGlow ? `url(#${glowId})` : undefined}
      />

      {/* Subtle Specular Sheen for modern glossy feel */}
      {gradient && (
        <path
          d="M 12 16 C 12 13.791 13.791 12 16 12 L 84 12 C 86.209 12 88 13.791 88 16 L 88 22 C 88 22 55 22 45 27 C 35 32 12 34 12 34 Z"
          fill="white"
          opacity="0.22"
        />
      )}
    </svg>
  )
}

/**
 * Premium TRC Badge container:
 * Glassmorphic white surface, emerald border, layered shadow, and smooth hover animation.
 */
export function TrcLogoBadge({
  size = 'md',
  className = '',
}: {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}) {
  const sizeMap = {
    sm: { box: 'h-9 w-9 rounded-[10px]', icon: 20 },
    md: { box: 'h-11 w-11 rounded-[13px]', icon: 26 },
    lg: { box: 'h-14 w-14 rounded-[16px]', icon: 34 },
    xl: { box: 'h-16 w-16 rounded-[20px]', icon: 40 },
  }

  const { box, icon } = sizeMap[size]

  return (
    <div
      style={{
        background: 'linear-gradient(145deg, #ffffff 0%, #f4fbf0 100%)',
        border: '1.5px solid rgba(110, 190, 72, 0.45)',
        boxShadow: '0 4px 14px -1px rgba(110, 190, 72, 0.22), 0 2px 6px -1px rgba(5, 55, 54, 0.08)',
      }}
      className={`group flex shrink-0 items-center justify-center transition-all duration-200 hover:scale-105 hover:shadow-[0_6px_20px_rgba(110,190,72,0.32)] ${box} ${className}`}
    >
      <TrcArrowLogo
        size={icon}
        className="transition-transform duration-200 group-hover:scale-110"
      />
    </div>
  )
}
